import React, { useEffect, useState } from "react";
import {
  createMemberBulkUpload,
  getJumuia,
  getSmsBalance,
} from "../../services/api";

import { getCurrentUser } from "../../services/auth";
import { toast } from "react-toastify";

const estimateBulkSmsUsage = (
  totalMembers = 0,
  estimatedUnitsPerSms = 1
) => {
  return (
    Number(totalMembers || 0) *
    Number(
      estimatedUnitsPerSms || 1
    )
  );
};

export default function BulkMemberUploadModal({
  onClose,
  onSuccess,
}) {
  const user = getCurrentUser();

  const [file, setFile] = useState(null);
  const [estimatedRows, setEstimatedRows] = useState(0);
  const [uploading, setUploading] =
    useState(false);

  const [result, setResult] =
    useState(null);

  const [jumuiaList, setJumuiaList] =
    useState([]);

  const [selectedJumuia, setSelectedJumuia] =
    useState("");

  const [smsBalance, setSmsBalance] =
    useState(0);

  const [loadingBalance, setLoadingBalance] =
    useState(true);

  const parokiaId = user?.parokiaId;

  /* =========================================
     LOAD INITIAL DATA
  ========================================= */
  useEffect(() => {
    loadJumuia();
    loadSmsBalance();
  }, []);

  /* =========================================
     LOAD SMS BALANCE
  ========================================= */
  const loadSmsBalance = async () => {
    try {
      setLoadingBalance(true);

      const res = await getSmsBalance();

      setSmsBalance(
        Number(
          res?.data?.balance || 0
        )
      );
    } catch (err) {
      console.error(err);
      setSmsBalance(0);
    } finally {
      setLoadingBalance(false);
    }
  };

  /* =========================================
     LOAD JUMUIA
  ========================================= */
  const loadJumuia = async () => {
    try {
      const res = await getJumuia(
        parokiaId
      );

      const rows = Array.isArray(
        res.data
      )
        ? res.data
        : [];

      setJumuiaList(rows);
    } catch (err) {
      console.error(err);

      toast.error(
        "Failed to load Jumuia"
      );
    }
  };

  /* =========================================
     DOWNLOAD TEMPLATE
  ========================================= */
  const downloadSampleCsv = () => {
    if (smsBalance <= 0) {
      toast.error(
        "Cannot process member creation until SMS top up"
      );

      return;
    }

    if (!selectedJumuia) {
      toast.error(
        "Please select Jumuiya"
      );

      return;
    }

    const selected =
      jumuiaList.find(
        (j) =>
          String(j.jumuia_id) ===
          String(selectedJumuia)
      );

    if (!selected) {
      toast.error(
        "Invalid Jumuia selected"
      );

      return;
    }

    const headers = [
      "kanda_id",
      "kanda_name",
      "jumuia_id",
      "jumuia_name",
      "first_name",
      "middle_name",
      "surname",
      "gender",
      "date_of_birth",
      "phone",
      "marital_status",
      "address",
      "country_name",
      "region_name",
      "district_name",
      "age_group",
    ];

    const sampleRows = [
      [
        selected.kanda_id,
        selected.kanda_name || "",
        selected.jumuia_id,
        selected.jumuia_name || "",

        "John",
        "Peter",
        "Mushi",
        "MALE",
        "1998-05-10",
        "255712345678",
        "SINGLE",
        "Kimara mwisho",
        "Tanzania",
        "Dar es Salaam",
        "Ubungo",
        "YOUTH",
      ],

      [
        selected.kanda_id,
        selected.kanda_name || "",
        selected.jumuia_id,
        selected.jumuia_name || "",

        "Maria",
        "Anna",
        "Joseph",
        "FEMALE",
        "1995-08-21",
        "255754321000",
        "MARRIED",
        "Mbagala",
        "Tanzania",
        "Dar es Salaam",
        "Temeke",
        "ADULT",
      ],
    ];

    const csv = [
      headers.join(","),
      ...sampleRows.map((row) =>
        row.join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url =
      URL.createObjectURL(blob);

    const a =
      document.createElement("a");

    a.href = url;

    a.download = `${selected.jumuia_name}_member_upload_template.csv`;

    a.click();

    URL.revokeObjectURL(url);
  };

  /* =========================================
     UPLOAD
  ========================================= */
  const handleUpload = async () => {
    if (smsBalance <= 0) {
      toast.error(
        "Cannot process member creation until SMS top up"
      );

      return;
    }

    if (!file) {
      toast.error(
        "Please select CSV file"
      );

      return;
    }

    const formData = new FormData();

    formData.append("file", file);

    try {
      setUploading(true);

      const res =
        await createMemberBulkUpload(
          formData
        );

      setResult(res.data);

      if (
        res.data?.successCount > 0
      ) {
        toast.success(
          `Bulk upload completed. Success: ${res.data.successCount}, Failed: ${res.data.failedCount}`
        );
      } else {
        toast.error(
          "Bulk upload finished with no successful records"
        );
      }

      // if (
      //   typeof res.data
      //     ?.remainingSmsBalance !==
      //   "undefined"
      // ) {
      //   setSmsBalance(
      //     Number(
      //       res.data
      //         .remainingSmsBalance
      //     )
      //   );
      // }
      setTimeout(async () => { 
        await loadSmsBalance(); 
      }, 300);
      onSuccess?.();
    } catch (err) {
      console.error(err);

      toast.error(
        err?.response?.data?.message ||
          "Bulk upload failed"
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal-overlay">
      {/* =========================================
          UPLOADING LOADER
      ========================================= */}
      {uploading && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background:
              "rgba(15,23,42,0.65)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 16,
            backdropFilter:
              "blur(2px)",
          }}
        >
          <div
            style={{
              width: 70,
              height: 70,
              border:
                "6px solid rgba(255,255,255,0.15)",
              borderTop:
                "6px solid #8b5cf6",
              borderRadius: "50%",
              animation:
                "spin 1s linear infinite",
            }}
          />

          <div
            style={{
              color: "#fff",
              fontSize: 18,
              fontWeight: 600,
            }}
          >
            Uploading members...
          </div>

          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      )}

      <div
        className="modal"
        style={{
          width: "95%",
          maxWidth: "950px",
        }}
      >
        <div
          className="modal-close"
          onClick={onClose}
        >
          ✕
        </div>

        <h3>Bulk Member Upload</h3>

        {/* =========================================
            SMS BALANCE STATUS
        ========================================= */}
        <div
          style={{
            marginBottom: 18,
            padding: "14px 16px",
            borderRadius: 10,
            fontWeight: 600,

            background:
              smsBalance > 0
                ? "rgba(16,185,129,0.12)"
                : "rgba(239,68,68,0.12)",

            border:
              smsBalance > 0
                ? "1px solid rgba(16,185,129,0.35)"
                : "1px solid rgba(239,68,68,0.35)",

            color:
              smsBalance > 0
                ? "#10b981"
                : "#ef4444",
          }}
        >
          {loadingBalance
            ? "Checking SMS balance..."
            : smsBalance > 0
            ? `SMS Balance Available: ${smsBalance}`
            : "Cannot process member creation until SMS top up"}
        </div>

        {/* =========================================
            SELECT JUMUIA
        ========================================= */}
        <div className="modal-section">
          <h4>Select Jumuiya</h4>

          <select
            className="input"
            value={selectedJumuia}
            onChange={async (e) => {
              const selected =
                e.target.files?.[0] || null;

              setFile(selected);

              if (selected) {
                const text =
                  await selected.text();

                const rows =
                  text
                    .split("\n")
                    .filter((r) => r.trim());

                // minus CSV header
                setEstimatedRows(
                  Math.max(0, rows.length - 1)
                );
              } else {
                setEstimatedRows(0);
              }
            }}
            disabled={
              smsBalance <= 0
            }
          >
            <option value="">
              -- Select Jumuiya --
            </option>

            {jumuiaList.map((j) => (
              <option
                key={j.jumuia_id}
                value={j.jumuia_id}
              >
                {j.jumuia_name}
                {" - "}
                {j.kanda_name}
              </option>
            ))}
          </select>
        </div>

        {/* =========================================
            FILE SECTION
        ========================================= */}
        <div className="modal-section">
          <h4>Upload CSV</h4>

          <div className="form-grid">
            <div
              style={{
                gridColumn:
                  "1 / span 2",
              }}
            >
              <input
                type="file"
                accept=".csv"
                className="input"
                disabled={
                  smsBalance <= 0
                }
                onChange={(e) =>
                  setFile(
                    e.target.files?.[0] ||
                      null
                  )
                }
              />
            </div>
          </div>

          <div
            style={{
              marginTop: 12,
              display: "flex",
              gap: 10,
            }}
          >
            <button
              className="btn primary"
              onClick={
                downloadSampleCsv
              }
              disabled={
                smsBalance <= 0
              }
            >
              Download CSV Template
            </button>

            <button
              className="btn primary"
              onClick={handleUpload}
              disabled={
                uploading ||
                smsBalance <= 0
              }
            >
              {uploading
                ? "Uploading..."
                : "Upload"}
            </button>
            {estimatedRows > 0 && (
  <div
    style={{
      marginTop: 12,
      padding: 12,
      borderRadius: 10,
      background:
        "rgba(99,102,241,0.08)",
      border:
        "1px solid rgba(99,102,241,0.2)",
      fontSize: 14,
    }}
  >
    <div>
      Estimated Members:{" "}
      <strong>
        {estimatedRows}
      </strong>
    </div>

    <div>
      Estimated SMS Units:{" "}
      <strong>
        {estimatedRows}
      </strong>
    </div>

    <div>
      Current SMS Balance:{" "}
      <strong>
        {smsBalance}
      </strong>
    </div>

    {estimatedRows >
      smsBalance && (
      <div
        style={{
          marginTop: 8,
          color: "#ef4444",
          fontWeight: 600,
        }}
      >
        Warning: SMS balance may
        not be enough for all
        members.
      </div>
    )}
  </div>
)}
          </div>
        </div>

        {/* =========================================
            RESULTS
        ========================================= */}
        {result && (
          <div className="modal-section">
            <h4>Upload Result</h4>

            <div
              className="card"
              style={{
                padding: 12,
              }}
            >
              <p>
                <strong>Total:</strong>{" "}
                {result.total}
              </p>

              <p>
                <strong>Success:</strong>{" "}
                {
                  result.successCount
                }
              </p>

              <p>
                <strong>Failed:</strong>{" "}
                {result.failedCount}
              </p>
            </div>

            <div
              style={{
                maxHeight: "250px",
                overflowY: "auto",
                marginTop: 10,
              }}
            >
              <table className="table">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Status</th>
                    <th>Member Code</th>
                    <th>Reason</th>
                    <th>SMS</th>
                  </tr>
                </thead>

                <tbody>
                  {(
                    result.results || []
                  ).map((r, i) => (
                    <tr key={i}>
                      <td>{r.row}</td>

                      <td>
                        {r.status}
                      </td>

                      <td>
                        {r.memberCode ||
                          "-"}
                      </td>

                      <td>
                        {r.reason ||
                          "-"}
                      </td>

                      <td>
                        {r.smsStatus ||
                          "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}